import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { FaBoxes, FaEdit, FaPlus, FaSearch, FaTimes, FaTrash } from "react-icons/fa";
import { getProductImageUrl, handleProductImageError } from "../../../util/storeLogoHelper";
import "../product/AdminProducts.css";
import "../Inventory/AdminInventory.css";
import BundlesGuide from "./BundlesGuide";

const money = (value) => `Rs. ${Math.round(Number(value || 0)).toLocaleString()}`;
// Price of one bundle row: its chosen option, or "from" the cheapest option.
const itemPrice = (item) => {
  const options = item.variants || [];
  if (!options.length) return Number(item.price || 0);
  const pinned = options.find((option) => option.id === item.variant_id);
  return pinned ? pinned.final_price : Math.min(...options.map((option) => option.final_price));
};
const optionTitle = (item) => (item.variants || []).find((option) => option.id === item.variant_id)?.title;

const EMPTY = { name: "", description: "", discount_type: "percent", discount_value: 10, status: true, starts_at: "", ends_at: "", items: [] };

/**
 * Bundles: products sold together at a discount. Checkout applies the
 * discount once every product of a bundle is in the cart, and shows it on the
 * product page as "Bundle and save".
 */
export default function AdminBundles() {
  const [bundles, setBundles] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    axios.get("/api/admin/bundles").then((res) => {
      if (res.data.status === 200) setBundles(res.data.bundles || []);
    }).catch(() => setBundles([]));
  }, []);

  useEffect(() => {
    document.title = "Bundles & offers — Admin";
    load();
  }, [load]);

  const remove = (bundle) => {
    swal({ title: `Delete "${bundle.name}"?`, text: "Shoppers stop getting this bundle's discount.", icon: "warning", buttons: ["Cancel", "Delete"], dangerMode: true })
      .then((ok) => {
        if (!ok) return;
        axios.delete(`/api/admin/bundles/${bundle.id}`).then(load).catch(() => swal("Error", "Could not delete the bundle.", "error"));
      });
  };

  return (
    <div className="ap">
      <div className="ap-head">
        <div>
          <h4>Bundles &amp; offers</h4>
          <p>Get shoppers to buy more in each order: discounted bundles, suggestions on the product and cart pages, and a one-click offer after checkout.</p>
        </div>
        <div className="ap-head-actions">
          <button type="button" className="ap-btn ap-btn-primary" onClick={() => setEditing({ ...EMPTY })}><FaPlus size={11} /> New bundle</button>
        </div>
      </div>

      <BundlesGuide />

      <div className="d-flex align-items-end justify-content-between gap-2 flex-wrap mb-2">
        <div>
          <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
            Your bundles {bundles ? <span className="ap-count">{bundles.length}</span> : null}
          </h5>
          <p className="text-muted small mb-0">Active bundles show as "Bundle and save" on the product page of every product in them.</p>
        </div>
      </div>

      <div className="ap-card">
        <div className="ap-table-wrap">
          <table className="ap-table" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>Bundle</th>
                <th>Products</th>
                <th>Price</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bundles === null ? (
                [0, 1, 2].map((row) => (
                  <tr key={row}>{[160, 180, 90, 70, 80].map((width, cell) => <td key={cell}><span className="ap-skel" style={{ width, height: 14 }} /></td>)}</tr>
                ))
              ) : bundles.length ? bundles.map((bundle) => (
                <tr key={bundle.id}>
                  <td>
                    <div className="ap-product-name" style={{ maxWidth: 240 }}>{bundle.name}</div>
                    <div className="ap-product-meta">
                      {bundle.discount_type === "percent" ? `${bundle.discount_value}% off` : `${money(bundle.discount_value)} off`}
                      {bundle.starts_at || bundle.ends_at ? ` · ${bundle.starts_at || "now"} → ${bundle.ends_at || "no end"}` : ""}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center gap-1">
                      {bundle.items.map((item, index) => (
                        <span key={`${item.product_id}-${index}`} title={`${item.qty} × ${item.name}${optionTitle(item) ? ` (${optionTitle(item)})` : item.variants?.length ? " (any option)" : ""}${item.available ? "" : " (not on sale)"}`} style={{ position: "relative", opacity: item.available ? 1 : 0.4 }}>
                          <img className="ap-thumb" style={{ width: 38, height: 38 }} src={getProductImageUrl(item.image)} alt="" onError={handleProductImageError} />
                          {item.qty > 1 ? <span className="ap-thumb-count">{item.qty}</span> : null}
                        </span>
                      ))}
                    </div>
                    {bundle.items.some((item) => !item.available) ? <div className="ap-variant-note">A product is not on sale, so shoppers cannot see this bundle.</div> : null}
                  </td>
                  <td>
                    <div className="ap-price">{money(bundle.value - bundle.discount)}</div>
                    <div className="ap-cost"><s>{money(bundle.value)}</s> · saves {money(bundle.discount)}</div>
                  </td>
                  <td>{bundle.status ? <span className="ap-badge ap-badge-green">Active</span> : <span className="ap-badge ap-badge-muted">Off</span>}</td>
                  <td>
                    <div className="ap-actions">
                      <button type="button" className="ap-icon-btn" onClick={() => setEditing({ ...bundle, items: bundle.items.map((item) => ({ ...item })) })} aria-label={`Edit ${bundle.name}`}><FaEdit size={13} /></button>
                      <button type="button" className="ap-icon-btn is-danger" onClick={() => remove(bundle)} aria-label={`Delete ${bundle.name}`}><FaTrash size={12} /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5">
                    <div className="ap-empty">
                      <div className="ap-empty-icon"><FaBoxes /></div>
                      <h6>No bundles yet</h6>
                      <p className="mb-1">A bundle is 2 or more products sold together at a discount, e.g. "Shirt + Trousers, 10% off".</p>
                      <p className="mb-3">Pair products that sell well together, or move older stock by bundling it with a best seller.</p>
                      <button type="button" className="ap-btn ap-btn-primary" onClick={() => setEditing({ ...EMPTY })}><FaPlus size={11} /> New bundle</button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PostPurchaseSettings />

      {editing ? <BundleDialog bundle={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} /> : null}
    </div>
  );
}

function BundleDialog({ bundle, onClose, onSaved }) {
  const [form, setForm] = useState(bundle);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Product search uses the admin product list (live products only).
  useEffect(() => {
    const text = query.trim();
    if (!text) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      axios.get(`/api/view-products?page=1&per_page=10&status=on_store&q=${encodeURIComponent(text)}`)
        .then((res) => setResults(res.data.products || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  // Products with sizes or colors load their options, so a row can be tied to one of them.
  const add = (product) => {
    const existing = form.items.find((item) => item.product_id === product.id);
    if (existing && !existing.variants?.length) return;
    setQuery("");
    axios.get(`/api/admin/bundles/product-variants/${product.id}`)
      .catch(() => ({ data: {} }))
      .then((res) => {
        const variants = res.data.variants || [];
        setForm((current) => ({
          ...current,
          items: [...current.items, {
            product_id: product.id,
            variant_id: null,
            variants,
            qty: 1,
            name: product.name,
            image: product.image,
            price: product.selling_price,
            available: true,
          }],
        }));
      });
  };
  const setItem = (index, changes) => set("items", form.items.map((row, at) => (at === index ? { ...row, ...changes } : row)));
  const value = form.items.reduce((sum, item) => sum + itemPrice(item) * Number(item.qty || 1), 0);
  const discount = form.discount_type === "percent"
    ? value * Math.min(90, Math.max(0, Number(form.discount_value) || 0)) / 100
    : Math.min(value, Number(form.discount_value) || 0);

  const save = (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description,
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value) || 0,
      status: !!form.status,
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
      items: form.items.map((item) => ({ product_id: item.product_id, variant_id: item.variant_id || null, qty: Number(item.qty) || 1 })),
    };
    const request = form.id ? axios.put(`/api/admin/bundles/${form.id}`, payload) : axios.post("/api/admin/bundles", payload);
    request.then((res) => {
      if (res.data.status === 200) onSaved();
      else swal("Not saved", res.data.message || "Check the bundle and try again.", "warning");
    }).catch((err) => swal("Not saved", err.response?.data?.message || "Check the bundle and try again.", "warning"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="inv-modal-backdrop" role="presentation" onClick={onClose}>
      <form className="inv-modal" style={{ maxWidth: 620 }} role="dialog" aria-modal="true" aria-label="Bundle" onClick={(event) => event.stopPropagation()} onSubmit={save}>
        <div className="inv-modal-head">
          <div>
            <h5>{form.id ? "Edit bundle" : "New bundle"}</h5>
            <p>Shoppers get the discount when all these products are in their cart.</p>
          </div>
          <button type="button" className="ap-icon-btn" onClick={onClose} aria-label="Close"><FaTimes size={12} /></button>
        </div>
        <div className="inv-modal-body">
          <label className="form-label fw-semibold small mb-1" htmlFor="bundle-name">Name</label>
          <input id="bundle-name" className="form-control mb-3" value={form.name} onChange={(event) => set("name", event.target.value)} placeholder="e.g. Winter set: jacket + tracksuit" required />

          <label className="form-label fw-semibold small mb-1" htmlFor="bundle-desc">Line shown to shoppers <span className="text-muted fw-normal">(optional)</span></label>
          <input id="bundle-desc" className="form-control mb-3" value={form.description || ""} onChange={(event) => set("description", event.target.value)} placeholder="Buy together and save" />

          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="form-label fw-semibold small mb-1" htmlFor="bundle-type">Discount</label>
              <select id="bundle-type" className="form-select" value={form.discount_type} onChange={(event) => set("discount_type", event.target.value)}>
                <option value="percent">Percent off the bundle</option>
                <option value="fixed">Amount off the bundle (Rs.)</option>
              </select>
            </div>
            <div className="col-6">
              <label className="form-label fw-semibold small mb-1" htmlFor="bundle-value">{form.discount_type === "percent" ? "Percent (up to 90)" : "Amount (Rs.)"}</label>
              <input id="bundle-value" type="number" min="0" max={form.discount_type === "percent" ? 90 : undefined} step="0.01" className="form-control" value={form.discount_value} onChange={(event) => set("discount_value", event.target.value)} required />
            </div>
            <div className="col-6">
              <label className="form-label fw-semibold small mb-1" htmlFor="bundle-start">Starts <span className="text-muted fw-normal">(optional)</span></label>
              <input id="bundle-start" type="date" className="form-control" value={form.starts_at || ""} onChange={(event) => set("starts_at", event.target.value)} />
            </div>
            <div className="col-6">
              <label className="form-label fw-semibold small mb-1" htmlFor="bundle-end">Ends <span className="text-muted fw-normal">(optional)</span></label>
              <input id="bundle-end" type="date" className="form-control" value={form.ends_at || ""} onChange={(event) => set("ends_at", event.target.value)} />
            </div>
          </div>

          <label className="form-label fw-semibold small mb-1" htmlFor="bundle-search">Products (2 to 10)</label>
          <div className="ap-search mb-2">
            <FaSearch size={13} />
            <input id="bundle-search" className="form-control" placeholder="Search products to add" value={query} onChange={(event) => setQuery(event.target.value)} autoComplete="off" />
          </div>
          {results.length > 0 && (
            <div className="border rounded-3 mb-2" style={{ maxHeight: 180, overflowY: "auto" }}>
              {results.map((product) => {
                const added = form.items.some((item) => item.product_id === product.id && !item.variants?.length);
                return (
                  <button type="button" key={product.id} disabled={added} onClick={() => add(product)} className="d-flex align-items-center gap-2 w-100 border-0 bg-transparent text-start px-2 py-1" style={{ opacity: added ? 0.5 : 1 }}>
                    <img className="ap-thumb" style={{ width: 32, height: 32 }} src={getProductImageUrl(product)} alt="" onError={handleProductImageError} />
                    <span className="flex-grow-1 text-truncate small">{product.name}</span>
                    <span className="small fw-semibold">{added ? "Added" : money(product.selling_price)}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="d-grid gap-2 mb-3">
            {form.items.map((item, index) => (
              <div key={`${item.product_id}-${index}`} className="d-flex align-items-center gap-2 border rounded-3 p-2 flex-wrap">
                <img className="ap-thumb" style={{ width: 38, height: 38 }} src={getProductImageUrl(item.image)} alt="" onError={handleProductImageError} />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="text-truncate small fw-semibold">{item.name}</div>
                  {item.variants?.length ? (
                    <select className="form-select form-select-sm mt-1" style={{ maxWidth: 260 }} value={item.variant_id || ""} aria-label={`Option for ${item.name}`}
                      onChange={(event) => setItem(index, { variant_id: event.target.value ? Number(event.target.value) : null })}>
                      <option value="">Any option (shopper picks)</option>
                      {item.variants.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.title} · {money(option.final_price)}{option.qty > 0 ? ` · ${option.qty} left` : " · sold out"}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>
                <span className="small text-muted">{item.variants?.length && !item.variant_id ? "from " : ""}{money(itemPrice(item))}</span>
                <input type="number" min="1" max="20" className="form-control form-control-sm" style={{ width: 64 }} value={item.qty} aria-label={`Quantity of ${item.name}`}
                  onChange={(event) => setItem(index, { qty: Math.max(1, Math.min(20, parseInt(event.target.value, 10) || 1)) })} />
                <button type="button" className="ap-icon-btn is-danger" onClick={() => set("items", form.items.filter((row, at) => at !== index))} aria-label={`Remove ${item.name}`}><FaTimes size={11} /></button>
              </div>
            ))}
            {form.items.length < 2 && <div className="small text-muted">Add at least 2 products.</div>}
            {form.items.some((item) => item.variants?.length) && (
              <div className="small text-muted">
                "Any option" lets the shopper pick the size or color. Pick one option to sell only that one in this bundle. Add a product again to bundle two of its options.
              </div>
            )}
          </div>

          <div className="inv-calc">
            <div><span>Products</span><strong>{money(value)}</strong></div>
            <div className="text-muted">−</div>
            <div><span>Saving</span><strong style={{ color: "#15803d" }}>{money(discount)}</strong></div>
            <div className="text-muted">=</div>
            <div><span>Shopper pays</span><strong style={{ color: "var(--ap-accent)" }}>{money(value - discount)}</strong></div>
          </div>

          <label className="d-flex align-items-center gap-2 small fw-semibold">
            <input type="checkbox" checked={!!form.status} onChange={(event) => set("status", event.target.checked)} /> Active (shoppers can get this bundle)
          </label>
        </div>
        <div className="inv-modal-foot">
          <button type="button" className="ap-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="ap-btn ap-btn-primary" disabled={saving || form.items.length < 2 || !form.name.trim()}>
            {saving ? "Saving…" : "Save bundle"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * One-click offer on the order confirmation page: the shopper can add one
 * discounted suggestion to a cash-on-delivery order before it is processed.
 */
function PostPurchaseSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get("/api/admin/post-purchase")
      .then((res) => res.data.status === 200 && setForm(res.data.settings))
      .catch(() => {});
  }, []);

  if (!form) return null;
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const save = (event) => {
    event.preventDefault();
    setSaving(true);
    axios.put("/api/admin/post-purchase", form)
      .then((res) => {
        if (res.data.status === 200) {
          setForm(res.data.settings);
          swal("Saved", res.data.message, "success");
        }
      })
      .catch((error) => swal("Error", error.response?.data?.message || "Could not save.", "error"))
      .finally(() => setSaving(false));
  };

  return (
    <form id="post-purchase-settings" className="ap-card mt-4 p-4" onSubmit={save} style={{ scrollMarginTop: 80 }}>
      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
        <div>
          <h5 className="fw-bold mb-1">Post-purchase offer</h5>
          <p className="text-muted small mb-0" style={{ maxWidth: 560 }}>
            Shown on the "Thank you" page right after a shopper places an order. No email or SMS is sent.
            They see up to 3 suggested products at your extra discount and a countdown. One click adds the product to the
            same order, and it is collected on delivery. If they leave the page or the time runs out, the offer is gone.
            Only for cash on delivery orders not yet processed. Products with sizes or colors are not offered.
          </p>
        </div>
        <label className="d-flex align-items-center gap-2 small fw-semibold">
          <input type="checkbox" checked={!!form.enabled} onChange={(event) => set("enabled", event.target.checked)} /> Turned on
        </label>
      </div>
      <div className="row g-3">
        <div className="col-md-5">
          <label className="form-label small fw-semibold">Heading</label>
          <input className="form-control form-control-sm" maxLength={80} value={form.title} onChange={(event) => set("title", event.target.value)} required />
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label small fw-semibold">Extra discount (%)</label>
          <input type="number" min="0" max="90" step="1" className="form-control form-control-sm" value={form.discount}
            onChange={(event) => set("discount", Math.max(0, Math.min(90, Number(event.target.value) || 0)))} />
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label small fw-semibold">Offer lasts (minutes)</label>
          <input type="number" min="1" max="1440" className="form-control form-control-sm" value={form.windowMinutes}
            onChange={(event) => set("windowMinutes", Math.max(1, Math.min(1440, parseInt(event.target.value, 10) || 1)))} />
        </div>
      </div>
      <div className="d-flex justify-content-end mt-3">
        <button type="submit" className="ap-btn ap-btn-primary" disabled={saving || !form.title.trim()}>{saving ? "Saving…" : "Save offer"}</button>
      </div>
    </form>
  );
}
